import React, { memo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import GlassCard from '../card/GlassCard';
import { defaultChartOptions, stillageScanningCols } from 'services/glass-management/glassManagement';
import dayjs from 'dayjs';
import { themeColors } from 'helpers/utils';
import SLAChart from '../SLAChart';
import { openModal } from 'slices/glassDashboard/glassDataModalSlice';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'react-tooltip';

const InformationBtn = () => {
    return (
        <div
            className='d-flex position-absolute w-100 bottom-0 rounded align-items-center justify-content-end p-2'
            style={{ height: '40px', }}>
            <div className='pull-right'>
                <Tooltip id="my-tooltip" />
                <FontAwesomeIcon
                    size='lg'
                    icon='info-circle'
                    className='me-2 text-info'
                    data-tooltip-id="my-tooltip"
                    data-tooltip-place="top"
                    data-tooltip-html={`
                        <h5 class="text-center text-light">This Widget Is For Information Only</h5>
                        <p>As Padiham stillage data is not managed through EVA, this widget only measures 
                        stillages scanned at Emplas before 9:00AM</p>`
                    }
                />
            </div>
        </div>
    )
}

const ScanningOff = () => {
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector(s => s.stillage);
    if (error)
        toast.error('Something went wrong!', error);

    const buildChartData = useCallback((key) => {
        const day = data[key];
        if (!day) return [];
        const {
            // expected = [], 
            received: { onTime = [], notOnTime = [] }
        } = day;

        // const expDict = expected?.reduce((acc, curr) => {
        //     acc[curr.stillageNo] = curr;
        //     return acc;
        // }, {});
        const onTimeDict = onTime?.reduce((acc, curr) => {
            acc[curr.stillageNo] = curr;
            return acc;
        }, {});
        const notOnTimeDict = notOnTime?.reduce((acc, curr) => {
            acc[curr.stillageNo] = curr;
            return acc;
        }, {});
        const stillageSet = new Set([
            // ...expected?.map(x => x?.stillageNo) || [],
            ...onTime?.map(x => x?.stillageNo) || [],
            ...notOnTime?.map(x => x?.stillageNo) || []
        ]?.filter(x => x != null));

        const drillDown = Array.from(stillageSet)?.map(stillageNo => {
            // const exp = Object.hasOwn(expDict || {}, stillageNo) ? expDict[stillageNo] : {};
            const onTime = Object.hasOwn(onTimeDict || {}, stillageNo) ? onTimeDict[stillageNo] : {};
            const notOnTime = Object.hasOwn(notOnTimeDict || {}, stillageNo) ? notOnTimeDict[stillageNo] : {};
            const scanned = onTime?.deliveryDate || notOnTime?.deliveryDate;

            return {
                // deliveryDate: exp?.deliveryDate ? dayjs(exp.deliveryDate).format('DD/MM/YYYY') : 'N/A',
                scannedDate: scanned ? dayjs(scanned).format('DD/MM/YYYY HH:mm:ss') : 'N/A',
                stillageNo
            };
        });
        return drillDown;
    }, [data]);

    const onChartClick = useCallback((e) => {
        if (data && e?.name) {
            dispatch(openModal({
                modalData: buildChartData(e.name),
                modalLoading: false,
                modalShow: true,
                modalTitle: 'Stillage Scanning Off',
                tableCols: stillageScanningCols,
            }));
        }
    }, [dispatch, data]);

    const getOptions = useCallback(() => {
        const xAxis = Object.keys(data || {});
        // const expected = xAxis.map((x, idx) => {
        //     return {
        //         value: data[x]?.expected?.length || 0,
        //         name: xAxis[idx],
        //     }
        // });
        const onTime = xAxis.map((x, idx) => {
            return {
                value: data[x]?.received?.onTime?.length || 0,
                name: xAxis[idx],
            }
        });
        const notOnTime = xAxis.map((x, idx) => {
            return {
                value: data[x]?.received?.notOnTime?.length || 0,
                name: xAxis[idx],
            }
        });

        return {
            ...defaultChartOptions,
            xAxis: {
                ...defaultChartOptions.xAxis,
                data: xAxis?.map(x => dayjs(x).format('ddd DD'))
            },
            tooltip: {
                trigger: 'axis',
                show: true,
                axisPointer: { type: 'shadow' },
                formatter: params => {
                    // const expected = params.find(x => x.seriesName === 'Expected');
                    const ontime = params.find(x => x.seriesName === 'On Time');
                    const notontime = params.find(x => x.seriesName === 'Not On Time');
                    return `<div style="display: flex; justify-content: space-between; flex-direction: column">
                        <h6>${dayjs(params[0].name).format('DD/MM/YYYY')}</h6>` +
                        // <span>${expected?.marker} Expected: <b>${expected?.value || 0}</b></span>
                        `<span>${ontime?.marker} On Time: <b>${ontime?.value || 0}</b></span>
                        <span>${notontime?.marker} Not On Time: <b>${notontime?.value || 0}</b></span>
                    </div>`;
                }
            },
            legend: {
                data: [//'Expected', 
                    'On Time', 'Not On Time'],
            },
            series: [
                // {
                //     type: 'bar',
                //     name: 'Expected',
                //     data: expected,
                //     barGap: '-100%',
                //     z: 1,
                //     itemStyle: {
                //         color: rgbaColor(themeColors.secondary, 0.3)
                //     },
                //     emphasis: { itemStyle: { color: rgbaColor(themeColors.secondary, 0.5) } }
                // },
                {
                    type: 'bar',
                    name: 'On Time',
                    data: onTime,
                    itemStyle: {
                        color: themeColors.success
                    }
                },
                {
                    type: 'bar',
                    name: 'Not On Time',
                    data: notOnTime,
                    itemStyle: {
                        color: themeColors.danger
                    }
                },
            ],
        }
    }, [data]);

    const options = getOptions();
    return (
        <GlassCard md={6} lg={4} title="Emplas Stillage Delivery Times">
            <SLAChart loading={loading} options={options} onClick={onChartClick} />
            <InformationBtn />
        </GlassCard>
    );
}

export default memo(ScanningOff);